import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import * as adminApi from '../../api/admin';
import AdminLayout from './AdminLayout';
import AdminUserModal from './AdminUserModal';
import styles from './AdminUsers.module.css';

const PAGE_SIZE_OPTIONS = [20, 50, 100];

const getStatusLabel = (user) => {
  if (user.is_blocked) return { label: 'Заблокирован', type: 'blocked' };
  if (user.blocked_until && new Date(user.blocked_until) > new Date())
    return { label: 'Вр. заблокирован', type: 'temp-blocked' };
  return { label: 'Активен', type: 'active' };
};

const formatDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const AdminUsers = () => {
  const { token } = useAuth();

  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 20, totalPages: 1 });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Фильтры и сортировка
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [status, setStatus] = useState('');
  const [role, setRole] = useState('');
  const [sortBy, setSortBy] = useState('registered_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [limit, setLimit] = useState(20);
  const [page, setPage] = useState(1);

  // Модалка
  const [selectedUser, setSelectedUser] = useState(null);

  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const data = await adminApi.getUsers(token, { page, limit, search, status, role, sortBy, sortOrder });
      setUsers(data.users);
      setPagination(data.pagination);
    } catch (err) {
      setError(err.response?.data?.message || 'Ошибка загрузки пользователей');
    } finally {
      setIsLoading(false);
    }
  }, [token, page, limit, search, status, role, sortBy, sortOrder]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setPage(1);
  };

  const handleLimitChange = (newLimit) => {
    setLimit(newLimit);
    setPage(1);
  };

  const handleExport = async () => {
    try {
      await adminApi.downloadCsv(token);
    } catch {
      alert('Ошибка экспорта CSV');
    }
  };

  const [syncLoading, setSyncLoading] = useState(false);
  const handleSyncChat = async () => {
    setSyncLoading(true);
    try {
      await adminApi.syncChat(token);
      alert('Синхронизация запущена в фоновом режиме');
    } catch {
      alert('Ошибка запуска синхронизации');
    } finally {
      setSyncLoading(false);
    }
  };

  const handleUserUpdate = (updatedUser) => {
    setUsers((prev) => prev.map((u) => (u.id === updatedUser.id ? updatedUser : u)));
    setSelectedUser(updatedUser);
  };

  const sortIcon = (field) => {
    if (sortBy !== field) return ' ↕';
    return sortOrder === 'asc' ? ' ↑' : ' ↓';
  };

  return (
    <AdminLayout>
      <div className={styles.page}>
        <div className={styles.header}>
          <h1 className={styles.title}>Пользователи</h1>
          <button className={styles.exportBtn} onClick={handleSyncChat} disabled={syncLoading}>
            {syncLoading ? 'Запуск...' : 'Синхронизировать чат'}
          </button>
          <button className={styles.exportBtn} onClick={handleExport}>
            Экспорт CSV
          </button>
        </div>

        {/* Фильтры */}
        <div className={styles.filters}>
          <form onSubmit={handleSearch} className={styles.searchForm}>
            <input
              className={styles.searchInput}
              type="text"
              placeholder="Поиск по email, имени, ID..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            <button type="submit" className={styles.searchBtn}>Найти</button>
          </form>

          <div className={styles.filterRow}>
            <select
              className={styles.select}
              value={status}
              onChange={(e) => { setStatus(e.target.value); setPage(1); }}
            >
              <option value="">Все статусы</option>
              <option value="active">Активные</option>
              <option value="blocked">Заблокированные</option>
            </select>

            <select
              className={styles.select}
              value={role}
              onChange={(e) => { setRole(e.target.value); setPage(1); }}
            >
              <option value="">Все роли</option>
              <option value="admin">Администраторы</option>
              <option value="user">Пользователи</option>
            </select>

            <select
              className={styles.select}
              value={limit}
              onChange={(e) => handleLimitChange(Number(e.target.value))}
            >
              {PAGE_SIZE_OPTIONS.map((s) => (
                <option key={s} value={s}>{s} на странице</option>
              ))}
            </select>
          </div>
        </div>

        {/* Ошибка */}
        {error && <div className={styles.error}>{error}</div>}

        {/* Таблица */}
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.th}>
                  <button className={styles.sortBtn} onClick={() => handleSort('email')}>
                    Email{sortIcon('email')}
                  </button>
                </th>
                <th className={styles.th}>Имя</th>
                <th className={styles.th}>Роль</th>
                <th className={styles.th}>Статус</th>
                <th className={styles.th}>
                  <button className={styles.sortBtn} onClick={() => handleSort('registered_at')}>
                    Регистрация{sortIcon('registered_at')}
                  </button>
                </th>
                <th className={styles.th}>
                  <button className={styles.sortBtn} onClick={() => handleSort('pay_until')}>
                    Оплачено до{sortIcon('pay_until')}
                  </button>
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={6} className={styles.loadingCell}>Загрузка...</td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={6} className={styles.emptyCell}>Пользователи не найдены</td>
                </tr>
              ) : (
                users.map((user) => {
                  const statusInfo = getStatusLabel(user);
                  return (
                    <tr
                      key={user.id}
                      className={styles.row}
                      onClick={() => setSelectedUser(user)}
                    >
                      <td className={styles.td}>
                        <span className={styles.email}>{user.email}</span>
                        {user.force_password_change && (
                          <span className={styles.forceBadge} title="Требуется смена пароля">⚠</span>
                        )}
                      </td>
                      <td className={styles.td}>
                        {[user.first_name, user.last_name].filter(Boolean).join(' ') || '—'}
                      </td>
                      <td className={styles.td}>
                        <span className={`${styles.roleBadge} ${user.is_admin ? styles.roleAdmin : styles.roleUser}`}>
                          {user.is_admin ? 'Админ' : 'Пользователь'}
                        </span>
                      </td>
                      <td className={styles.td}>
                        <span className={`${styles.statusBadge} ${styles[statusInfo.type]}`}>
                          {statusInfo.label}
                        </span>
                      </td>
                      <td className={styles.td}>{formatDate(user.registered_at)}</td>
                      <td className={styles.td}>
                        {user.pay_until ? (
                          <span className={new Date(user.pay_until) > new Date() ? styles.payActive : styles.payExpired}>
                            {formatDate(user.pay_until)}
                          </span>
                        ) : '—'}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Пагинация */}
        <div className={styles.pagination}>
          <span className={styles.paginationInfo}>
            Всего: {pagination.total} | Страница {pagination.page} из {pagination.totalPages}
          </span>
          <div className={styles.paginationBtns}>
            <button
              className={styles.pageBtn}
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
            >
              ← Назад
            </button>
            <button
              className={styles.pageBtn}
              disabled={page >= pagination.totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Вперёд →
            </button>
          </div>
        </div>
      </div>

      {/* Модалка пользователя */}
      {selectedUser && (
        <AdminUserModal
          user={selectedUser}
          token={token}
          onClose={() => setSelectedUser(null)}
          onUpdate={handleUserUpdate}
          onRefresh={fetchUsers}
        />
      )}
    </AdminLayout>
  );
};

export default AdminUsers;
